import type { Lang, LorsCopy } from "./translations";

export type Oct15Headline = { headline: string; subhead: string };

export const oct15Headlines: Oct15Headline[] = [
  {
    headline: "Your family shouldn't have to figure this out in a courtroom",
    subhead:
      "One hour to learn how a trust keeps your home — and your family — out of probate.",
  },
  {
    headline: "The conversation your family hopes you'll have before they need to",
    subhead:
      "Learn the estate planning basics that save your loved ones months of stress, cost, and confusion.",
  },
  {
    headline: "Grief is hard enough without a judge involved",
    subhead:
      "Find out how California homeowners are keeping their families out of probate court — in one free hour.",
  },
  {
    headline: "In California, your home doesn't automatically go to your family",
    subhead:
      "Learn what Sonoma County homeowners need to know about trusts, probate, and protecting what you've built.",
  },
  {
    headline: "Most Sonoma County homes are valuable enough to trigger probate",
    subhead:
      "Understand the one document that changes everything — and why a will alone won't cut it in California.",
  },
  {
    headline: "California probate can take over a year. Your family pays every month.",
    subhead:
      "Learn how a trust keeps your home out of the courts and in the hands of the people you choose.",
  },
  {
    headline: "A will doesn't keep your home out of probate. A lot of people find that out too late.",
    subhead:
      "Join Ruby Steinbrecher for a free workshop on the documents California homeowners actually need.",
  },
  {
    headline: "Naming a beneficiary isn't the same as having a plan",
    subhead:
      "What most families think protects them — and what actually does. One hour, no jargon, free.",
  },
  {
    headline: "\"We have a will\" is the most expensive sentence in California probate court",
    subhead:
      "Learn why a trust works differently — and how to set one up without the overwhelm.",
  },
  {
    headline: "Probate costs California families thousands of dollars and over a year of waiting",
    subhead:
      "Find out how one planning session can save your family all of it.",
  },
  {
    headline: "Every month without a plan is a month your family could spend in court",
    subhead:
      "A free one-hour workshop on the estate planning basics every homeowner should know.",
  },
  {
    headline: "The cost of probate goes up every year your home gains value",
    subhead:
      "Learn how to protect your biggest asset — and your family — before it becomes someone else's problem.",
  },
  {
    headline: "One hour. One plan. A lifetime of peace of mind.",
    subhead:
      "Join Ruby Steinbrecher for a free estate planning workshop — and leave knowing your family is protected.",
  },
  {
    headline: "The families who sleep best are the ones who already handled it",
    subhead:
      "Learn how a trust protects your home, simplifies everything for your family, and takes less effort than you think.",
  },
  {
    headline: "This is the thing you'll be glad you didn't put off",
    subhead:
      "A free, no-pressure workshop on estate planning for California homeowners. One hour. Real answers.",
  },
];

const baseEn = {
  eyebrow: "Free Estate Planning Workshop",
  headline: oct15Headlines[0].headline,
  headlineBreakBefore: "",
  subhead: oct15Headlines[0].subhead,
  subheadLabel: "A note from Ruby:",
  learnTitle: "What Most Families Don't Realize Until It's Too Late",
  learnItems: [
    "How wills, living trusts, and powers of attorney each work in California",
    "How a properly funded living trust can help a family pass assets without probate court",
    "What California's probate process generally looks like — timelines, court involvement, and typical costs",
    "Practical first steps for putting an estate plan in place, and the questions to ask before you do",
  ],
  quote:
    "Ruby Steinbrecher was fantastic. The family trust workshop was clear, informative, and extremely helpful for getting started with estate planning.",
  quoteAttribution: "Shawn H., Workshop Attendee",
  formDate: { short: "Thu, October 15th", long: "Thursday, October 15th" },
  formTime: "4:00–5:00 PM",
  formVenueLine1: "North Coast Builder Exchange",
  formVenueLine2: "1030 Apollo Way, Santa Rosa, CA 95407",
  formTitle: "Reserve Your Seat",
  formSubtitle: "Free to attend. Limited seating available.",
  formFirstName: "First name",
  formLastName: "Last name",
  formEmail: "Email",
  formPhone: "Phone",
  formSmsConsent:
    "I consent to receive text messages from the Law Office of Ruby Steinbrecher about this event, and occasional marketing messages. Message & data rates may apply. Text HELP for assistance, reply STOP to opt out.",
  formEnewsConsent:
    "I'd like to receive occasional email updates from the Law Office of Ruby Steinbrecher.",
  formPrivacy: "Privacy Policy",
  formSubmit: "Reserve My Seat",
  formSubmitting: "Reserving your seat…",
  formTrust: "⭐ Trusted by local families | 81 Five-Star Reviews",
  getDirections: "Get Directions",
  footerCopyright: "© 2026 Ruby Steinbrecher",
  footerPrivacy: "Privacy Policy",
  testimonialsTitle: "What Families Are Saying",
  aboutTitle: "Meet Ruby Steinbrecher",
  aboutRole: "Attorney",
  aboutBody: [
    "I have practiced law since 2007, first working at a medium-sized firm in San Francisco handling civil litigation, and then moving into estate planning, small business and contract law since 2010. I value people, the planet, and prosperity that does not adversely affect the former.",
    "Prior to entering the legal field, I had a variety of business and life experience that has only enhanced my ability to serve clients: Road Manager for Ken Kesey\'s bus trip to Great Britain in 1999, Human Resources at a tech startup in 2001, recognized for my work as a Case Manager at Casey Family Programs with disadvantaged youth in the foster care system in Oakland and the wider San Francisco-Bay Area in the early 2000\'s.",
    "Currently, I live in Sebastopol with my daughter, husband, and a menagerie of pets, where I enjoy gardening as it keeps me grounded and enjoying the beautiful weather.",
  ],
  registrationTitle: "Ready to Secure Your Family's Future?",
  registrationSub: "Free to attend. Limited seating available.",
  registrationCta: "Save My Seat",
  registrationFoot:
    "No pressure. Just clear information so you can make the right decision for your family.",
  errorTitle: "Something went wrong",
  errorBody:
    "We couldn't reserve your seat. Please try again or call our office.",
  validation: {
    firstName: "First name is required",
    lastName: "Last name is required",
    email: "Enter a valid email",
    phone: "Enter a valid phone number",
  },
  timeAgo: {
    monthAgo: "a month ago",
    monthsAgo: (n: number) => `${n} months ago`,
    yearAgo: "a year ago",
  },
};

export const lorsContentOct15 = { en: baseEn, es: baseEn } as unknown as Record<Lang, LorsCopy>;

export const OCT15_DIRECTIONS_URL =
  "https://www.google.com/maps/dir/?api=1&destination=North+Coast+Builders+Exchange,+1030+Apollo+Way,+Santa+Rosa,+CA+95407";

export const getOct15Copy = (hParam: string | null): LorsCopy => {
  const n = Number(hParam);
  const idx = Number.isInteger(n) && n >= 1 && n <= oct15Headlines.length ? n - 1 : 0;
  const pick = oct15Headlines[idx];
  return { ...baseEn, headline: pick.headline, subhead: pick.subhead } as unknown as LorsCopy;
};
